import type React from 'react';
import { Suspense, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from '@/components/sidebar';
import { Header } from '@/components/header';
import { ViewProvider } from '@/contexts/view-context';
import { TrainerProvider } from '@/contexts/trainer-context';
import { MobileProvider } from '@/contexts/mobile-context';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { ConfirmationProvider } from '@/components/providers/confirmation-provider';
import { FixPointerEventsButton } from '@/components/debug/fix-pointer-events-button';
import { useServerEvents } from '@/hooks/useServerEvents';

function DashboardContent({ children }: { children: React.ReactNode }) {
  useServerEvents();
  const [showDebugButton, setShowDebugButton] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const timer = setTimeout(() => setShowDebugButton(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <div className="flex flex-1 relative">
        <div className="fixed top-0 bottom-0 left-0 w-[240px] z-10 hidden md:block">
          <Sidebar className="h-full" />
        </div>
        <main className="flex-1 ml-0 md:ml-[240px] mt-0 p-6 overflow-auto">
          <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
        </main>
      </div>
      <ConfirmationProvider />
      {showDebugButton && <FixPointerEventsButton />}
    </div>
  );
}

export default function DashboardLayout() {
  return (
    <ProtectedRoute>
      <ViewProvider>
        <TrainerProvider>
          <MobileProvider>
            <DashboardContent>
              <Outlet />
            </DashboardContent>
          </MobileProvider>
        </TrainerProvider>
      </ViewProvider>
    </ProtectedRoute>
  );
}
